import React, {useMemo} from 'react'
import {
	getLayoutData,
	getWWDPageData,
	getWWDPageDataModulesOrder,
	getWWDPageDataPreviewModules,
	getWWDType
} from "@/pages/api/api";
import {
	AB_VARIANT_BLOCK_QUERY,
	CONTEXT_BASED_BANNER_QUERY,
	RECAP_INFO_QUERY,
	RECAP_SUMMARY_ITEM_QUERY,
	RECAP_SUMMARY_ITEM_QUERY_PREVIEW_MODULES,
	SEE_ALSO_INFO_BLOCK_QUERY,
	SEO_QUERY,
	STATE_QUERY,
	WHAT_WE_DO_CATEGORY_QUERY,
	WHAT_WE_DO_SINGLE_ACTION_QUERY
} from "@/pages/api/fragments";
import {AB_PREFERRED_COOKIE, AB_VARIANTS, CONTENT_TYPES, PAGES} from "@/constants";
import {dynamicWithLoader, getItemType, getRandomABVariant, partition} from "@/utils/utils";

const WhatWeDoCategoryPageTemplate = dynamicWithLoader(() => import('@/templates/WhatWeDoCategoryPageTemplate'));
const WhatWeDoSingleActionPageTemplate = dynamicWithLoader(() => import('@/templates/WhatWeDoSingleActionPageTemplate'), );


function getTemplate(data) {
	const type = getItemType(data)
	switch (type) {
		case CONTENT_TYPES.WHAT_WE_DO_CATEGORY:
			return WhatWeDoCategoryPageTemplate
		case CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION:
			return WhatWeDoSingleActionPageTemplate
		default: // default in case typename doesn't match
			return () => null
	}
}

export default function Page({ data }) {
	const Template = useMemo(() => getTemplate(data), [data])
	return <Template data={data} />
}

const CATEGORY_TYPE = 'allWhatWeDoCategories'
const ACTION_TYPE = 'allWhatWeDoSingleActions'

const PATHS_QUERY = `
	query GetWhatWeDoCategories($locale: SiteLocale) {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: { slug: { neq: "${PAGES.WHAT_WE_DO}"  } },
			first: 500
		) {
            pageName
            slug
		}
	}
`;
const ACTION_PATHS_QUERY = `
	query GetWhatWeDoCategories($locale: SiteLocale) {
		${ACTION_TYPE}(
			locale: $locale,
			first: 500
		) {
            slug
		}
	}
`;

const TYPE_QUERY = `
	query GetWWDType($slug: String, $locale: SiteLocale)  {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
			first: 500
		) {
			__typename
			slug
        }
        ${ACTION_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
			first: 500
		) {
			__typename
			slug
        }
    }
`


const CATEGORY_PROPS_QUERY = `
	query GetWWDCategory($slug: String, $locale: SiteLocale)  {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
			first: 500
		) {
			__typename
			id
			pageName
			slug
			${AB_VARIANT_BLOCK_QUERY}
			actions: _allReferencingWhatWeDoSingleActions {
				${WHAT_WE_DO_SINGLE_ACTION_QUERY}
				state: chooseState {
					${STATE_QUERY}
				}
			}
			${SEO_QUERY}
			recapInfo {
				${RECAP_INFO_QUERY}
			}
			content {
				id
				${RECAP_SUMMARY_ITEM_QUERY}
			}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			isHorizontalAspect
			seeAlsoLinks {
				... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
					${WHAT_WE_DO_SINGLE_ACTION_QUERY}
				}
				... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
					${WHAT_WE_DO_CATEGORY_QUERY}
				}
			}
        }
	}
`

const CATEGORY_PROPS_MODULES_IDS_QUERY = `
	query GetWWDCategory($slug: String, $locale: SiteLocale)  {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
			first: 500
		) {
			__typename
			content {
				id
			}
        }
	}
`

const CATEGORY_PROPS_PREVIEW_MODULES_QUERY = `
	query GetWWDCategory($slug: String, $locale: SiteLocale)  {
		${CATEGORY_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
			first: 500
		) {
			__typename
			id
			pageName
			slug
			${AB_VARIANT_BLOCK_QUERY}
			content {
				id
				${RECAP_SUMMARY_ITEM_QUERY_PREVIEW_MODULES}
			}
			isHorizontalAspect
        }
	}
`
const ACTION_PROPS_QUERY = `
	query GetWWDCategory($slug: String, $locale: SiteLocale)  {
		${ACTION_TYPE}(
			locale: $locale, 
			filter: { slug: { eq: $slug } },
			first: 500
		) {
			__typename
			name
			slug
			${AB_VARIANT_BLOCK_QUERY}
			${SEO_QUERY}
			${CONTEXT_BASED_BANNER_QUERY}
			recapInfo{
				${RECAP_INFO_QUERY}
			}
			content{
				${RECAP_SUMMARY_ITEM_QUERY}
			}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			isHorizontalAspect
			seeAlsoLinks {
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
				${WHAT_WE_DO_SINGLE_ACTION_QUERY}
			  }
			  ... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
				${WHAT_WE_DO_CATEGORY_QUERY}
			  }
			}
		}
	}
`


function formatProps({ data, layout, locale, preview}){
	return ({
		data,
		locale,
		...layout,
		isDarkSearch: true,
		isSingleWhatWeDoPage: getItemType(data) === CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION,
		preview
	})
}

// export async function getStaticProps({ params, locale, preview=false }) {
// 	const { slug } = params;
// 	try {
// 		let data = {}
// 		const [category, actions] = await getWWDType(slug, locale, TYPE_QUERY, [CATEGORY_TYPE, ACTION_TYPE], preview)
// 		if(category.length > 0){
// 			//is category
// 			data = await getPageData(CATEGORY_TYPE, slug, CATEGORY_PROPS_QUERY, locale, '', preview);
// 		} else if(actions.length > 0){
// 			//is action
// 			data = await getPageData(ACTION_TYPE, slug, ACTION_PROPS_QUERY, locale, '', preview)
// 		} else {
// 			return { notFound: true }; // go to 404
// 		}
//
// 		const layout = await getLayoutData(locale, preview)
// 		return {
// 			props: formatProps({ data, layout, locale, preview }),
// 			revalidate: 60 * 2
// 		};
// 	} catch (err){
// 		console.error('error for slug:', params.slug, err);
// 		return { notFound: true }; // go to 404
// 	}
// }

// export async function getStaticPaths({ locales }) {
// 	const paths = [];
// 	for (const locale of locales) {
// 		const categoryPage = await getPathsForLocale(CATEGORY_TYPE, PATHS_QUERY, locale);
// 		paths.push(...categoryPage);
//
// 		const actionPage = await getPathsForLocale(ACTION_TYPE, ACTION_PATHS_QUERY, locale);
// 		paths.push(...actionPage);
// 	}
//
// 	return {
// 		paths,
// 		// fallback: false,
// 		fallback: 'blocking',
// 	};
// }
//
// async function getPathsForLocale(type, query, locale) {
// 	const pages = await getPages(type, query, locale);
//
// 	return pages.map((page) => {
// 		return {
// 			params: {
// 				slug: page.slug,
// 			},
// 			locale,
// 		};
// 	}).filter((el) => {
// 		return el !== null;
// 	});
// }


function getABVariant(req) {
	//look for preferred variant
	const preferred_cookie = req.cookies[AB_PREFERRED_COOKIE];
	if (preferred_cookie === AB_VARIANTS.A || preferred_cookie === AB_VARIANTS.B) return preferred_cookie; //return it if exists

	//return randomized variant
	return getRandomABVariant()
}

function setDataByVariant(data, variant){
	let resData, v, index
	const split = partition(data, (page) => page?.abVariant === AB_VARIANTS.A)
	if(variant === AB_VARIANTS.A){
		index = 0 //use A data
	} else {
		index = 1 //use B data
	}
	resData = split[index]?.[0] //check data for current variant
	if(!resData){
		//if there is no data for given variant use another one
		index = index === 0 ? 1 : 0 //inverse variant
		resData = split[index]?.[0]
	}
	v = index === 0 ? AB_VARIANTS.A : AB_VARIANTS.B
	// logTable({ suggestedV: variant, finalV: v })
	return [resData, v]
}

function deepMerge(obj1, obj2) {
	if (!obj1) return obj2;
	if (!obj2) return obj1;
	const result = { ...obj1 };
	for (const key in obj2) {
		if (
			obj2.hasOwnProperty(key) &&
			typeof obj2[key] === 'object' &&
			obj2[key] !== null &&
			!Array.isArray(obj2[key])
		) {
			result[key] = deepMerge(obj1[key], obj2[key]);
		} else {
			result[key] = obj2[key];
		}
	}
	return result;
}

function mergeAndSortContentWithFullStructure(data1, data2, idArrays) {
	// Pick the first category record from data1 as base structure
	const res = []
	data1.forEach((data, index) => {
		const baseRecord = data1[index] || {};
		const ids = idArrays[index]
		// Combine all modules from data1 and data2 content arrays
		const allModules = [...(data1[index]?.content || []), ...(data2[index]?.content || [])];

		// Create a map to merge modules by id
		const moduleMap = new Map();

		allModules.forEach(module => {
			if (!module || !module.id) return;
			if (!moduleMap.has(module.id)) {
				moduleMap.set(module.id, module);
			} else {
				const existing = moduleMap.get(module.id);
				moduleMap.set(module.id, {
					...existing,
					item: deepMerge(existing.item, module.item)
				});
			}
		});

		// Build sorted content array ordered by idsArray
		const sortedContent = ids
			.map(id => moduleMap.get(id))
			.filter(Boolean);

		// Return a new object with the full structure from baseRecord,
		// but content replaced by the sorted & merged modules
		res.push({
			...baseRecord,
			content: sortedContent
		})
	})

	return res
}



export async function getServerSideProps({ params, locale , req, res, preview=false }) {
	const { slug } = params;
	try {
		let data = {}
		const suggestedVariant = getABVariant(req);

		//check if wwd is category or action
		const [category, actions] = await getWWDType(slug, locale, TYPE_QUERY, [CATEGORY_TYPE, ACTION_TYPE], preview)

		if(category.length > 0){
			//is category
			const modulesOrderSourceOfTruth = await getWWDPageDataModulesOrder(CATEGORY_TYPE, CATEGORY_PROPS_MODULES_IDS_QUERY, { slug, locale }, '', preview);
			const idArrays = modulesOrderSourceOfTruth.map((source) => source.content.map(item => item.id))
			const data1 = await getWWDPageData(CATEGORY_TYPE, CATEGORY_PROPS_QUERY, { slug, locale }, '', preview);
			const data2 = await getWWDPageDataPreviewModules(CATEGORY_TYPE, CATEGORY_PROPS_PREVIEW_MODULES_QUERY, { slug, locale }, '', preview);
			data = mergeAndSortContentWithFullStructure(data1, data2, idArrays);

		} else if(actions.length > 0){
			//is action
			data  = await getWWDPageData(ACTION_TYPE, ACTION_PROPS_QUERY, { slug, locale }, '', preview)
		} else {
			return { notFound: true }; // go to 404
		}


		const [finalData, finalVariant] = setDataByVariant(data, suggestedVariant)

		if(!finalData) return { notFound: true }; // go to 404

		const layout = await getLayoutData(locale, preview)

		//set variant cookie
		res.setHeader('Set-Cookie', `ab_variant=${finalVariant}; Path=/; Max-Age=31536000`);

		// Cache the server-rendered response
		res.setHeader(
			'Cache-Control',
			's-maxage=60, stale-while-revalidate=300'
		);

		return {
			props: formatProps({ data: finalData, layout, locale, preview }),
		};
	} catch (err){
		console.error('error for slug:', params.slug, err);
		return { notFound: true }; // go to 404
	}
}



